// src/services/zreportService.js

import { supabase } from '../pages/supabaseClient';
import { format, parseISO, addDays } from 'date-fns';

/**
 * Helpers to sum values out of your JSON arrays.
 */
function getPartnerTotal(list, name, field) {
  return (list || [])
    .filter(p => p.name === name)
    .reduce((sum, p) => sum + (Number(p[field]) || 0), 0);
}
function getSupplierTotal(list, method) {
  return (list || [])
    .filter(s => s.method === method)
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
}

/**
 * Upserts a ZReport row, computing every aggregate and a true 
 * running balance + **cumulative** variance based on the previous day’s stored values:
 *
 *   yesterdayBalance = prevRow.cash_balance || 0
 *   prevVariance     = prevRow.variance     || 0
 *
 *   cash_in           = one_stop_cash + sum(partners.cash)
 *   supplierCash      = sum(suppliers where method='Cash')
 *   net_cash          = cash_in − supplierCash
 *
 *   cash_balance      = yesterdayBalance + net_cash
 *   dailyVariance     = total_cash_counted − cash_balance
 *   cumulativeVariance= prevVariance + dailyVariance
 *
 */
export async function createOrUpdateZReport(entry) {
  const {
    date,
    one_stop_card,
    one_stop_cash,
    online_partners,
    suppliers,
    delivery_fee,
    total_cash_counted,
    logged_by,
    logged_at,
    modified_by,
    modified_at,
  } = entry;

  // 1) fetch yesterday’s cash_balance and cumulative variance
  const yesterdayDate = format(addDays(parseISO(date), -1), 'yyyy-MM-dd');
  const { data: prev, error: prevErr } = await supabase
    .from('zreports')
    .select('cash_balance, variance')
    .eq('date', yesterdayDate)
    .single();
  if (prevErr && prevErr.code !== 'PGRST116') throw prevErr;
  const yesterdayBalance = Number(prev?.cash_balance || 0);
  const prevVariance     = Number(prev?.variance     || 0);

  // 2) compute partner & supplier totals
  const snappyCard    = getPartnerTotal(online_partners, 'Snappy',    'card');
  const snappyCash    = getPartnerTotal(online_partners, 'Snappy',    'cash');
  const justeatCard   = getPartnerTotal(online_partners, 'Just Eat',  'card');
  const justeatCash   = getPartnerTotal(online_partners, 'Just Eat',  'cash');
  const deliverooCard = getPartnerTotal(online_partners, 'Deliveroo', 'card');
  const deliverooCash = getPartnerTotal(online_partners, 'Deliveroo', 'cash');

  // 3) cash in & net cash
  const cashIn       = Number(one_stop_cash || 0)
                     + snappyCash + justeatCash + deliverooCash;
  const supplierCash = getSupplierTotal(suppliers, 'Cash');
  const supplierBank = getSupplierTotal(suppliers, 'Bank');
  const netCash      = cashIn - supplierCash;

  // 4) running balance & cumulative variance
  const cashBalance     = yesterdayBalance + netCash;
  const dailyVariance   = Number(total_cash_counted || 0) - cashBalance;
  const cumulativeVar   = prevVariance + dailyVariance;

  // 5) upsert all fields (requires UNIQUE(date) in your schema)
  const { error: upsertErr } = await supabase
    .from('zreports')
    .upsert({
      date,
      one_stop_card,
      one_stop_cash,
      online_partners,
      suppliers,
      delivery_fee,
      total_cash_counted,
      logged_by,
      logged_at,
      modified_by,
      modified_at,

      snappy_card:        snappyCard,
      snappy_cash:        snappyCash,
      justeat_card:       justeatCard,
      justeat_cash:       justeatCash,
      deliveroo_card:     deliverooCard,
      deliveroo_cash:     deliverooCash,
      cash_in:            cashIn,
      supplier_cash_out:  supplierCash,
      supplier_bank_out:  supplierBank,
      net_cash:           netCash,
      cash_balance:       cashBalance,
      variance:           cumulativeVar,   // store the cumulative variance
         snappy_total_product_sales: entry.snappy_total_product_sales,
    snappy_total_surcharges: entry.snappy_total_surcharges,
    snappy_refunds: entry.snappy_refunds,
    snappy_cash_refunds: entry.snappy_cash_refunds,
    snappy_total_product_sales_from_till: entry.snappy_total_product_sales_from_till,
    snappy_daily_till_sales: entry.snappy_daily_till_sales,
    snappy_total_refunds: entry.snappy_total_refunds,
    snappy_variance: entry.snappy_variance,
    },
    { onConflict: ['date'] }
  );

  if (upsertErr) throw upsertErr;
  return { cashBalance, cumulativeVar };
}
